import React from 'react';
import i18next from 'i18next';
import PCSC from '../../../utils/pcsc';
import UCMS from '../../../utils/ucms';
import { errorFactory, declareError } from '../../../utils/errors';
import { fetchDiagnosis } from '../../../utils/diagnosis';
import { deriveUrl, sleep } from '../../../utils/generic';
import {
    webpcscNoService,
    apiPath,
    ucmsConstants,
    buildDeviceDropdownDisplaySuccess,
    buildDeviceDropdownDisplayFailure,
    vscPlaceholder,
    atrCases
} from '../../../constants';
import { fetchDeviceAssociateQuery } from '../../../utils/walkways';
import DeviceAssociateWithData from './DeviceAssociateWithData';

/* *****************************************************************************************
  Warning: this file is a dragon. test carefully after any change.
    the main loop or engine of this component is the handlePcscOnChange function
    be careful while using state, with this component it is very easy
    for weird state changes to conflict or occur out of order
****************************************************************************************** */

const TAG = 'DeviceAssociate';

function deviceQuery(props, state, reader) {
    return reader.status === 'available' || reader.status === 'assigned'
        ? true
        : i18next.t(`errorMsgs$deviceSelection_status_${reader.status}`);
}

function displayOption(data) {
    return data.issuable === true
        ? buildDeviceDropdownDisplaySuccess(
              data,
              i18next.t('genericDeviceDropdownLabels$serialNo'),
          )
        : buildDeviceDropdownDisplayFailure(data);
}

// Maps an identify error to the localized "issuable" message shown for the reader. Extracted from
// identifyDevices (and kept as a switch) to keep that function's cognitive complexity within bounds;
// behavior is identical to the previous if/else-if chain.
function issuableErrorMessage(error) {
    switch (error.msg) {
        case 'E_SHARING_VIOLATION':
            return i18next.t('errorMsgs$atr_inUse');
        case 'Unsupported card':
            return i18next.t('errorMsgs$unsupportedDevice');
        case 'CUUID retrieval error':
            return i18next.t('errorMsgs$cuuidRetrievalError');
        default:
            return i18next.t('errorMsgs$unidentifiable');
    }
}

class DeviceAssociate extends React.Component {
    state = {
        selected: '',
        readers: {},
        interval: null,
        syncing: false,
        operable: [],
        inoperable: [],
    };

    componentDidMount() {
        this.pcscOnChange();
    }

    componentWillUnmount() {
        if (this.state.interval) {
            this.state.interval.stop();
            this.setState({ ...this.state, interval: null });
        }
    }

    setReader = (value) => {
        const empty =
            value === i18next.t('genericDeviceDropdownLabels$defaultOption') ||
            value === i18next.t('genericDeviceDropdownLabels$noDevicesOption');
        this.setState({
            ...this.state,
            selected: empty ? {} : value,
        });
        if (this.props.setReader) {
            this.props.setReader(this.state.readers[value]);
        }
    };

    setDeviceAssociateQuery = (json) => {
        const { readers } = this.state;
        Object.keys(readers).forEach((readerName) => {
            const reader = readers[readerName];
            json.forEach((device) => {
                if (device.serial === reader.serial) {
                    readers[readerName] = {
                        ...reader,
                        ...device,
                        groups: device.groups,
                        changed: false,
                    };
                    if (device.serial !== vscPlaceholder.serial) {
                        // -------- for vsc "null" CAN BE MADE CLEANER
                        readers[readerName].display = displayOption({
                            ...reader,
                            ...device,
                        });
                    }
                }
            });
        });
        this.setState({
            ...this.state,
            readers: {
                ...this.state.readers,
                ...readers,
            },
        });
        this.filterDevices(deviceQuery);
    };

    setDetectReaders = (added, modified, updatedReaders) => {
        // eslint-disable-next-line max-len, no-return-assign, no-param-reassign
        added.forEach(
            (reader) =>
                (modified[reader] =
                    modified[reader] === undefined ? null : modified[reader]),
        );
        const modifiedReaders = {};
        Object.keys(modified).forEach((reader) => {
            modifiedReaders[reader] = {
                ...[reader],
                issuable: i18next.t(atrCases[modified[reader]]) || true,
                atr: modified[reader],
                readerName: reader,
                label: reader,
                changed: true,
            };
        });
        // React 18 automatic batching (createRoot) defers setState in setTimeout/promise
        // callbacks, so this.state is stale across the async detect->identify->query chain.
        // Thread the reader set explicitly so each step sees the accumulated readers, and
        // return the merged set for the next step. Mirrors VscCreationSelection/Scanner.
        const newReaders = {
            ...(updatedReaders || this.state.readers),
            ...modifiedReaders,
        };
        this.setState({
            ...this.state,
            readers: newReaders,
        });
        return newReaders;
    };

    filterDevices = (check) => {
        const { readers } = this.state;
        Object.keys(readers).forEach((readerName) => {
            const reader = readers[readerName];
            if (
                reader.issuable === true ||
                reader.issuable === i18next.t('errorMsgs$unissuable')
            ) {
                reader.issuable = check(null, this.state, reader);
            }
        });
        console.debug(TAG, 'filterDevices readers:', readers);
        this.setState({
            ...this.state,
            ...readers,
        });
    };

    removeReaders = (removed, updatedReaders) => {
        const readers = { ...(updatedReaders || this.state.readers) };
        removed.forEach((readerName) => {
            delete readers[readerName];
        });
        this.setState({
            ...this.state,
            selected: removed.includes(this.state.selected)
                ? ''
                : this.state.selected,
            readers,
        });
        this.props.enrollingDispatch({
            type: 'SET_SELECTED_READER',
            reader: removed.includes(this.state.selected)
                ? null
                : readers[this.state.selected],
        });
        return readers;
    };

    handlePcscOnChange = async (added, removed, modified) => {
        console.debug('handlePcscOnChange state:', this.state);
        this.setState({ ...this.state, syncing: true });
        let updatedReaders = { ...this.state.readers };
        if (removed.length > 0) {
            updatedReaders = this.removeReaders(removed, updatedReaders);
            // eslint-disable-next-line no-param-reassign
            removed.forEach((reader) => delete modified[reader]);
        }
        updatedReaders = this.setDetectReaders(added, modified, updatedReaders);
        updatedReaders = await this.identifyDevices(updatedReaders);
        await this.queryDevices(updatedReaders);
        if (this.props.isHost) {
            await this.handleVsc();
        }
        this.setState({
            ...this.state,
            syncing: false,
        });
    };

    handleVsc = async () => {
        const { readers } = this.state;
        // disable this code for now to show the placeholder all the time.
        // Enable it once we can differentiate the VSC created by us
        // if (
        //     Object.keys(readers).some(
        //         (reader) =>
        //             readers[reader].subType === vscPlaceholder.subType &&
        //             readers[reader].status === 'available',
        //     )
        // )
        //     return;
        if (await PCSC.supports(vscPlaceholder.subType)) {
            vscPlaceholder.display =
                i18next.t('mappingsLabels$deviceSubTypes$vsc$label') ||
                vscPlaceholder.display;
            this.setState({
                ...this.state,
                readers: {
                    ...this.state.readers,
                    vsc: vscPlaceholder,
                },
            });
            await fetchDeviceAssociateQuery(
                this.setDeviceAssociateQuery,
                this.props.appDispatch,
                [vscPlaceholder.device],
                this.props.associateUser,
            );
        }
    };

    pcscOnChange = async () => {
        try {
            await fetchDiagnosis(this.props.appDispatch);
            await PCSC.ready;
            const interval = await PCSC.onChange(
                this.handlePcscOnChange,
                true,
                1500,
            );
            this.setState({
                ...this.state,
                interval,
            });
        } catch (error) {
            console.error(
                TAG,
                'device selection identifyDevices error:',
                error,
            );
            declareError(
                this.props.appDispatch,
                errorFactory.err(`${webpcscNoService}_ERROR`),
            );
        }
    };

    // Connects to one reader and runs the APDU identify. Returns the identify result, or null on
    // error (also recording the localized issuable message on the reader). Extracted from
    // identifyDevices so that function's cognitive complexity stays within bounds; the connect ->
    // atr-override -> apdu.run -> disconnect sequence and error handling are unchanged.
    runIdentify = async (readerName, readers) => {
        let reader = null;
        try {
            reader = await PCSC.connect(readerName);
            var atr = readers[readerName].atr;
            // Hard coded the atr to 'axiad.whfb' for whfb
            if (readerName.includes('Windows Hello for Business')) {
                atr = 'axiad.whfb';
            }
            return await UCMS.apdu.run(
                reader,
                ucmsConstants.actions.identify,
                atr,
                () => {},
                deriveUrl(apiPath),
            );
        } catch (error) {
            console.error('device selection identifyDevices error:', error);
            readers[readerName].issuable = issuableErrorMessage(error);
            return null;
        } finally {
            if (reader) await reader.disconnect();
        }
    };

    // open questions
    identifyDevices = async (updatedReaders) => {
        // eslint-disable-line consistent-return
        const readers = { ...(updatedReaders || this.state.readers) };
        await sleep(1000);
        for (const readerName of Object.keys(readers)) {
            // eslint-disable-line no-restricted-syntax, max-len
            // Skip whfb if it is not same user
            if (readerName.includes('Windows Hello for Business') && !this.props.isHost) {
                continue; // eslint-disable-line no-continue
            }
            if (readers[readerName].changed === false) {
                continue; // eslint-disable-line no-continue
            }
            if (readers[readerName].issuable !== true) {
                continue; // eslint-disable-line no-continue
            }
            const result = await this.runIdentify(readerName, readers); // eslint-disable-line no-await-in-loop
            let card = {};
            if (result !== null) {
                card = result.entries[0]?.result;
            }
            console.debug(TAG, 'identifyDevices card:', card);
            readers[readerName] = {
                ...readers[readerName],
                ...card,
                device: card,
                label: displayOption({ ...card, ...readers[readerName] }),
            };
        }
        this.setState({
            ...this.state,
            readers: {
                ...this.state.readers,
                ...readers,
            },
        });
        return readers;
    };

    queryDevices = async (updatedReaders) => {
        const readers = updatedReaders || this.state.readers;
        console.debug(TAG, 'queryDevices readers:', readers);
        const data = [];
        for (const readerName of Object.keys(readers)) {
            // eslint-disable-line no-restricted-syntax, max-len
            const reader = readers[readerName];
            if (
                reader.issuable === true &&
                reader.changed === true &&
                reader.device
            ) {
                data.push(reader.device);
            }
        }
        if (data.length > 0) {
            await fetchDeviceAssociateQuery(
                this.setDeviceAssociateQuery,
                this.props.appDispatch,
                data,
                this.props.associateUser,
            );
        }
    };

    render() {
        const { display } = this.props;
        return (
            <DeviceAssociateWithData
                display={display}
                readers={this.state.readers}
                defaultOption={i18next.t(
                    'genericDeviceDropdownLabels$defaultOption',
                )}
                setReader={this.setReader}
                syncing={this.state.syncing}
            />
        );
    }
}

export default DeviceAssociate;
